import React, { useEffect, useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowLeft, CheckCircle2, FilePlus2, LayoutDashboard, LogOut, Menu, Phone, UserRound, X } from "lucide-react";
import logo from "../assets/WhatsApp Image 2024-12-03 at 3.41.47 PM.jpeg";
import campus from "../assets/optimized/hero-2.jpg";
import { getAdmissionConfig, parentLogout } from "../lib/api";
import { SITE, cn, whatsappLink } from "../lib/site";
import useSession from "../lib/useSession";
import { WhatsAppIcon } from "../componentsM/WhatsAppButton";

export function useAdmissionConfig() {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    getAdmissionConfig()
      .then((c) => alive && setConfig(c))
      .catch((e) => alive && setError(e));
    return () => {
      alive = false;
    };
  }, []);
  return { config, error };
}

const AUTH_BRANDS = {
  admissions: {
    label: "Admissions Portal",
    headline: (
      <>
        Your child's journey at TIS <span className="text-gold-400">starts here.</span>
      </>
    ),
    points: [
      "Apply online for IB Continuum or Matriculation",
      "Upload documents from your phone",
      "Track every step and interview date in one place",
    ],
    help: "Assalam o Alaikum, I need help with the TIS admissions portal.",
  },
  alumni: {
    label: "Alumni Network",
    headline: (
      <>
        Once a TISian, <span className="text-gold-400">always a TISian.</span>
      </>
    ),
    points: [
      "Find classmates from your batch",
      "Share where life has taken you",
      "Hear about reunions and alumni events first",
    ],
    help: "Assalam o Alaikum, I need help with the TIS alumni portal.",
  },
};

/** Split-screen layout for sign in / sign up / password pages. */
export function AuthLayout({ title, subtitle, children, footer, backTo = "/", backLabel = "Back to website", brand = "admissions" }) {
  const b = AUTH_BRANDS[brand] || AUTH_BRANDS.admissions;
  return (
    <>
    {/* Kept outside the grid: as a grid child it took the first cell and pushed the page out of place. */}
    <ToastContainer position="top-center" autoClose={5000} />
    <div className="min-h-screen bg-slate-50 lg:grid lg:grid-cols-[1.05fr_1fr]">
      <aside className="relative hidden overflow-hidden bg-navy-900 lg:sticky lg:top-0 lg:block lg:h-screen lg:self-start">
        <img src={campus} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900/95 via-navy-800/80 to-navy-700/60" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="The International School" className="h-14 w-14 rounded-full bg-white object-contain p-1" />
            <span className="font-display text-lg font-bold leading-tight">
              The International School
              <span className="block text-sm font-medium text-gold-300">{b.label}</span>
            </span>
          </Link>
          <div className="max-w-md">
            <h2 className="font-display text-4xl font-extrabold leading-tight">{b.headline}</h2>
            <ul className="mt-8 space-y-4 text-navy-100">
              {b.points.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-gold-400" aria-hidden />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <p className="text-sm text-navy-200">
            Need help? Call {SITE.uan} or{" "}
            <a className="font-semibold text-white underline" href={whatsappLink(b.help)} target="_blank" rel="noreferrer">
              chat on WhatsApp
            </a>
            .
          </p>
        </div>
      </aside>
      <main className="flex min-h-screen flex-col px-4 py-6 sm:px-8 lg:px-16">
        <div className="flex items-center justify-between">
          <Link to={backTo} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-navy-700">
            <ArrowLeft className="h-4 w-4" aria-hidden /> {backLabel}
          </Link>
          <Link to="/" className="lg:hidden">
            <img src={logo} alt="The International School" className="h-12 w-12 object-contain" />
          </Link>
        </div>
        <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-10">
          <h1 className="font-display text-3xl font-extrabold text-navy-800">{title}</h1>
          {subtitle && <p className="mt-2 text-slate-600">{subtitle}</p>}
          <div className="mt-8">{children}</div>
          {footer && <div className="mt-8 text-center text-sm text-slate-600">{footer}</div>}
        </div>
      </main>
    </div>
    </>
  );
}

const NAV = [
  { to: "/portal", label: "My applications", icon: LayoutDashboard, end: true },
  { to: "/portal/apply", label: "New application", icon: FilePlus2 },
  { to: "/portal/account", label: "My account", icon: UserRound },
];

/** Signed-in parent area. Redirects to login when there is no session. */
export function PortalLayout() {
  const auth = useSession("parent");
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { config } = useAdmissionConfig();

  useEffect(() => setOpen(false), [location.pathname]);

  if (!auth?.access) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/admissions/login?next=${next}`} replace />;
  }

  const logout = () => {
    parentLogout();
    navigate("/admissions/login");
  };
  const wa = config?.whatsapp_number || SITE.whatsapp;

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <ToastContainer position="top-center" autoClose={5000} />
      <header className="sticky top-0 z-40 border-b-4 border-gold-400 bg-navy-800 text-white shadow">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
          <Link to="/portal" className="flex items-center gap-3">
            <img src={logo} alt="" className="h-10 w-10 rounded-full bg-white object-contain p-0.5" />
            <span className="font-display text-sm font-bold leading-tight sm:text-base">
              TIS Admissions
              <span className="block text-xs font-medium text-navy-200">Parent portal</span>
            </span>
          </Link>
          <nav className="hidden items-center gap-1 md:flex" aria-label="Portal">
            {NAV.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition",
                    isActive ? "bg-white/15 text-white" : "text-navy-100 hover:bg-white/10 hover:text-white"
                  )
                }
              >
                <Icon className="h-4 w-4" aria-hidden />
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-3 md:flex">
            <span className="max-w-[12rem] truncate text-sm text-navy-100">{auth.user?.username}</span>
            <button onClick={logout} className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-navy-100 hover:bg-white/10 hover:text-white">
              <LogOut className="h-4 w-4" aria-hidden /> Sign out
            </button>
          </div>
          <button className="rounded-lg p-2 hover:bg-white/10 md:hidden" onClick={() => setOpen((v) => !v)} aria-label="Menu" aria-expanded={open}>
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {open && (
          <nav className="border-t border-white/10 px-4 pb-4 md:hidden" aria-label="Portal mobile">
            {NAV.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn("flex items-center gap-3 rounded-lg px-3 py-3 text-base font-semibold", isActive ? "bg-white/15" : "hover:bg-white/10")
                }
              >
                <Icon className="h-5 w-5" aria-hidden /> {label}
              </NavLink>
            ))}
            <button onClick={logout} className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-3 text-base font-semibold hover:bg-white/10">
              <LogOut className="h-5 w-5" aria-hidden /> Sign out
            </button>
          </nav>
        )}
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6">
        <Outlet />
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-4 py-5 text-sm text-slate-600 sm:flex-row sm:px-6">
          <p>
            Questions about admissions? We're here to help.{" "}
            <Link to="/" className="font-semibold text-navy-700 hover:underline">
              Back to tis.edu.pk
            </Link>
          </p>
          <div className="flex items-center gap-3">
            <a href={`tel:${SITE.uanTel}`} className="inline-flex items-center gap-1.5 font-semibold text-navy-700 hover:underline">
              <Phone className="h-4 w-4" aria-hidden /> {config?.office_phone || SITE.uan}
            </a>
            <a
              href={whatsappLink("Assalam o Alaikum, I have a question about my child's admission.", wa)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-3 py-1.5 font-semibold text-white hover:bg-[#1ebe5a]"
            >
              <WhatsAppIcon className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function PageTitle({ title, subtitle, back, actions }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        {back && (
          <Link to={back.to} className="mb-2 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-navy-700">
            <ArrowLeft className="h-4 w-4" aria-hidden /> {back.label}
          </Link>
        )}
        <h1 className="font-display text-2xl font-extrabold text-navy-800 sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-slate-600">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}
